import { useEffect } from 'react';
import { FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const Lightbox = ({ images, selectedIndex, onClose, onNavigate }) => {
  const currentImage = images[selectedIndex];

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && selectedIndex > 0) {
        onNavigate(selectedIndex - 1);
      } else if (e.key === 'ArrowRight' && selectedIndex < images.length - 1) {
        onNavigate(selectedIndex + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, images.length, onClose, onNavigate]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handlePrevious = () => {
    if (selectedIndex > 0) {
      onNavigate(selectedIndex - 1);
    }
  };

  const handleNext = () => {
    if (selectedIndex < images.length - 1) {
      onNavigate(selectedIndex + 1);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-95 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-3 bg-white bg-opacity-10 hover:bg-opacity-20 text-white rounded-lg transition-all z-10"
        aria-label="Close lightbox"
      >
        <FaTimes className="w-6 h-6" />
      </button>

      {/* Image Counter */}
      <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg z-10">
        <span className="text-sm font-medium">
          {selectedIndex + 1} / {images.length}
        </span>
      </div>

      {/* Previous Button */}
      {selectedIndex > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handlePrevious();
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white bg-opacity-10 hover:bg-opacity-20 text-white rounded-lg transition-all z-10"
          aria-label="Previous image"
        >
          <FaChevronLeft className="w-6 h-6" />
        </button>
      )}

      {/* Next Button */}
      {selectedIndex < images.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleNext();
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white bg-opacity-10 hover:bg-opacity-20 text-white rounded-lg transition-all z-10"
          aria-label="Next image"
        >
          <FaChevronRight className="w-6 h-6" />
        </button>
      )}

      {/* Main Image Container */}
      <div 
        className="relative max-w-7xl max-h-[85vh] w-full h-full flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={currentImage.url}
          alt={currentImage.alt || currentImage.title || 'Full size image'}
          className="max-w-full max-h-full object-contain rounded-lg"
        />

        {/* Image Info */}
        {(currentImage.title || currentImage.description) && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
            {currentImage.title && currentImage.title !== currentImage.filename && (
              <h3 className="text-white text-xl font-semibold mb-2">
                {currentImage.title}
              </h3>
            )}
            {currentImage.description && (
              <p className="text-white text-sm opacity-90">
                {currentImage.description}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Lightbox;